import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronUp,
  Maximize2,
  FileWarning,
  ExternalLink,
} from "lucide-react";
import { Text } from "@core/text";
import ErrorBoundary from "./ErrorBoundary";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PDFViewerProps {
  url: string;
  className?: string;
}

interface PDFErrorFallbackProps {
  url: string;
}

const PDFErrorFallback = ({ url }: PDFErrorFallbackProps) => {
  const { t } = useTranslation();

  const handleOpenPDF = () => {
    window.open(url, "_blank");
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-gray-100 dark:bg-gray-700 rounded-lg h-full">
      <FileWarning className="w-16 h-16 text-rose-500 mb-4" />
      <Text variant="h3" className="mb-2 text-center">
        {t("common.pdfViewer.error.loadFailed")}
      </Text>
      <Text variant="p" status="subtle" className="text-center max-w-md mb-6">
        {t("common.pdfViewer.error.description")}
      </Text>
      <button
        onClick={handleOpenPDF}
        className="flex items-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg transition-colors"
      >
        <ExternalLink className="w-4 h-4" />
        {t("common.pdfViewer.openInNewTab", "Open PDF in New Tab")}
      </button>
    </div>
  );
};

const PDFViewerComponent = ({ url, className = "" }: PDFViewerProps) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isExpanded, setIsExpanded] = useState(true);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const handleFullScreen = () => {
    window.open(url, "_blank");
  };

  return (
    <div
      className={`bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden h-full flex flex-col ${className}`}
    >
      <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-600 sticky top-0 bg-gray-100 dark:bg-gray-700 z-10">
        <div className="flex items-center space-x-2">
          <svg
            className="h-5 w-5 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <Text variant={"span"} weight={"medium"}>
            {t("common.pdfViewer.preview")}
          </Text>
        </div>
        <div className="flex items-center space-x-4">
          {numPages && isExpanded && (
            <>
              <button
                onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1}
                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                <svg
                  className="h-5 w-5 text-gray-600 dark:text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <Text variant={"span"} status="subtle">
                {t("common.pdfViewer.pageInfo", {
                  current: pageNumber,
                  total: numPages,
                })}
              </Text>
              <button
                onClick={() =>
                  setPageNumber(Math.min(numPages, pageNumber + 1))
                }
                disabled={pageNumber >= numPages}
                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                <svg
                  className="h-5 w-5 text-gray-600 dark:text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}
          <button
            onClick={handleFullScreen}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <Maximize2 className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            )}
          </button>
        </div>
      </div>

      <div
        className={`overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent ${isExpanded ? "animate-fadeIn" : "hidden"}`}
      >
        <div className="p-4 flex justify-center">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600" />
              </div>
            }
            error={
              <div className="text-center p-8 text-red-500">
                {t("common.pdfViewer.error.loadFailed")}
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="max-w-full"
              scale={1}
            />
          </Document>
        </div>
      </div>
    </div>
  );
};

export const PDFViewer = (props: PDFViewerProps) => (
  <ErrorBoundary fallback={<PDFErrorFallback url={props.url} />}>
    <PDFViewerComponent {...props} />
  </ErrorBoundary>
);
